/**
 * PuzzlePlayer — learner runtime for scene_type / segment_type `puzzle`.
 *
 * Supports nine puzzle_formats with keyboard-accessible controls.
 * Checks answers via POST /scenario-os/puzzles/{scene_id}/check.
 * Does NOT auto-advance on check — shows feedback first; Continue is explicit.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const API_BASE = '/scenario-os/puzzles';
const TOKEN_KEY = 'scenario_os_token';
const DRAFT_PREFIX = 'teachiq:puzzle_draft:';

function getToken() {
  return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || '';
}

function authHeaders() {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function resolveMeta(scene) {
  return (
    scene?.domain_metadata ||
    scene?.scene_data?.domain_metadata ||
    scene?.metadata ||
    {}
  );
}

/** Strip internal ids like "(ai_orch_w0)" from learner-facing titles. */
export function cleanPuzzleTitle(raw) {
  if (!raw) return 'Puzzle';
  return String(raw)
    .replace(/\s*\((?:ai_[a-z0-9_]+)\)\s*/gi, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function seededShuffle(arr, seedStr) {
  const out = [...arr];
  let s = 0;
  const str = String(seedStr || 'puzzle');
  for (let i = 0; i < str.length; i++) s = (Math.imul(31, s) + str.charCodeAt(i)) | 0;
  s = Math.abs(s) || 1;
  for (let i = out.length - 1; i > 0; i--) {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    const j = Math.abs(s) % (i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function draftKey(sceneId) {
  return `${DRAFT_PREFIX}${sceneId}`;
}

function loadDraft(sceneId) {
  if (!sceneId) return null;
  try {
    const raw = localStorage.getItem(draftKey(sceneId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveDraft(sceneId, answerState, result) {
  if (!sceneId) return;
  try {
    localStorage.setItem(draftKey(sceneId), JSON.stringify({
      answerState,
      result: result || null,
      updatedAt: Date.now(),
    }));
  } catch { /* ignore */ }
}

function moveItem(list, from, to) {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function SequenceView({ items, order, onChange, disabled }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const onDragStart = (idx, e) => {
    if (disabled) return;
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  };
  const onDrop = (idx) => {
    if (disabled || dragIdx == null || dragIdx === idx) {
      setDragIdx(null);
      setOverIdx(null);
      return;
    }
    onChange(moveItem(order, dragIdx, idx));
    setDragIdx(null);
    setOverIdx(null);
  };

  return (
    <div>
      <p style={helpText}>
        Drag tiles to reorder, or use ↑ / ↓. Top = first step in the sequence.
      </p>
      <ol style={listStyle}>
        {order.map((id, idx) => {
          const label = items.find((i) => i.id === id)?.label || id;
          const active = overIdx === idx && dragIdx != null && dragIdx !== idx;
          return (
            <li
              key={id}
              draggable={!disabled}
              onDragStart={(e) => onDragStart(idx, e)}
              onDragOver={(e) => { e.preventDefault(); setOverIdx(idx); }}
              onDragLeave={() => setOverIdx((v) => (v === idx ? null : v))}
              onDrop={() => onDrop(idx)}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              style={{
                ...listItemStyle,
                cursor: disabled ? 'default' : 'grab',
                opacity: dragIdx === idx ? 0.55 : 1,
                outline: active ? '2px solid #38bdf8' : 'none',
              }}
            >
              <span style={{ color: '#64748b', fontSize: 12, width: 22 }}>{idx + 1}.</span>
              <span style={{ flex: 1 }}>{label}</span>
              <button type="button" style={btnSm} disabled={disabled || idx === 0} onClick={() => onChange(moveItem(order, idx, idx - 1))} aria-label="Move up">↑</button>
              <button type="button" style={btnSm} disabled={disabled || idx === order.length - 1} onClick={() => onChange(moveItem(order, idx, idx + 1))} aria-label="Move down">↓</button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function PairMatchView({ left, right, pairs, onChange, disabled, pairResults }) {
  const resultByLeft = useMemo(() => {
    const m = {};
    (pairResults || []).forEach((p) => { m[p.left_id] = p; });
    return m;
  }, [pairResults]);

  const setPair = (l, r) => {
    if (disabled) return;
    const next = pairs.filter((p) => p[0] !== l);
    if (r) next.push([l, r]);
    onChange(next);
  };
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <p style={helpText}>
        For each term on the left, pick the matching definition. Options are shuffled.
      </p>
      {left.map((l) => {
        const cur = (pairs.find((p) => p[0] === l.id) || [])[1] || '';
        const pr = resultByLeft[l.id];
        const border = pr
          ? (pr.is_correct ? '1px solid rgba(16,185,129,0.7)' : '1px solid rgba(248,113,113,0.7)')
          : undefined;
        return (
          <div key={l.id}>
            <label style={{ ...rowLabel, ...(border ? { padding: 8, borderRadius: 8, border } : {}) }}>
              <span style={{ minWidth: 140, fontWeight: 600 }}>{l.label}</span>
              <select
                value={cur}
                onChange={(e) => setPair(l.id, e.target.value)}
                style={selectStyle}
                aria-label={`Match for ${l.label}`}
                disabled={disabled}
              >
                <option value="">Select…</option>
                {right.map((r) => (
                  <option key={r.id} value={r.id}>{r.label}</option>
                ))}
              </select>
            </label>
            {pr && !pr.is_correct ? (
              <div style={pairExplainWrong}>
                <div><strong>Correct:</strong> {pr.correct_label}</div>
                {pr.explanation ? <div style={{ marginTop: 4 }}>{pr.explanation}</div> : null}
              </div>
            ) : null}
            {pr && pr.is_correct && pr.explanation ? (
              <div style={pairExplainOk}>{pr.explanation}</div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function SelectSetView({ options, selected, onChange, multi = true, disabled }) {
  const toggle = (id) => {
    if (disabled) return;
    if (!multi) {
      onChange([id]);
      return;
    }
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };
  return (
    <div style={{ display: 'grid', gap: 8 }} role="group">
      {options.map((opt) => {
        const on = selected.includes(opt.id);
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => toggle(opt.id)}
            aria-pressed={on}
            disabled={disabled}
            style={{ ...optionBtn, ...(on ? optionBtnOn : {}), ...(disabled ? { opacity: 0.75 } : {}) }}
          >
            {opt.label}
            {opt.meta ? <span style={metaHint}>{opt.meta}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

function ClassifyView({ riskOptions, actionOptions, risk, action, onRisk, onAction, disabled }) {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <p style={helpText}>Pick one risk level and one required control for the case.</p>
      <div>
        <div style={sectionTitle}>Risk level</div>
        <SelectSetView options={riskOptions} selected={risk ? [risk] : []} onChange={(s) => onRisk(s[0] || '')} multi={false} disabled={disabled} />
      </div>
      <div>
        <div style={sectionTitle}>Required control</div>
        <SelectSetView options={actionOptions} selected={action ? [action] : []} onChange={(s) => onAction(s[0] || '')} multi={false} disabled={disabled} />
      </div>
    </div>
  );
}

function LabelDiagramView({ hotspots, labels, mapping, onChange, disabled }) {
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <p style={helpText}>Label each hotspot. Dropdown options are shuffled.</p>
      {hotspots.map((h) => (
        <label key={h.id} style={rowLabel}>
          <span style={{ minWidth: 140, fontWeight: 600 }}>{h.label || h.id}</span>
          <select
            value={mapping[h.id] || ''}
            onChange={(e) => onChange({ ...mapping, [h.id]: e.target.value })}
            style={selectStyle}
            disabled={disabled}
          >
            <option value="">Label…</option>
            {labels.map((l) => (
              <option key={l.id} value={l.id}>{l.label}</option>
            ))}
          </select>
        </label>
      ))}
    </div>
  );
}

function GraphAssembleView({ nodes, edges, onEdgesChange, nodeOrder, onNodeOrderChange, disabled }) {
  const [linkFrom, setLinkFrom] = useState(null);
  const orderedNodes = (nodeOrder || []).map((id) => nodes.find((n) => n.id === id) || { id, label: id });

  const toggleLink = (nodeId) => {
    if (disabled) return;
    if (!linkFrom) {
      setLinkFrom(nodeId);
      return;
    }
    if (linkFrom === nodeId) {
      setLinkFrom(null);
      return;
    }
    const exists = edges.some((e) => e[0] === linkFrom && e[1] === nodeId);
    if (exists) {
      onEdgesChange(edges.filter((e) => !(e[0] === linkFrom && e[1] === nodeId)));
    } else {
      onEdgesChange([...edges, [linkFrom, nodeId]]);
    }
    setLinkFrom(null);
  };

  const suggestFromOrder = () => {
    if (disabled || !nodeOrder?.length) return;
    const suggested = [];
    for (let i = 0; i < nodeOrder.length - 1; i++) {
      suggested.push([nodeOrder[i], nodeOrder[i + 1]]);
    }
    onEdgesChange(suggested);
  };

  // Simple left-to-right layout positions for SVG arrows
  const positions = {};
  orderedNodes.forEach((n, i) => {
    positions[n.id] = { x: 40 + i * 150, y: 70 };
  });

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <div>
        <div style={sectionTitle}>Part A — Order the stages</div>
        <p style={helpText}>
          Drag tiles into the pipeline sequence (left/top = start). This is the order stages appear in the flow.
        </p>
        <SequenceView
          items={nodes}
          order={nodeOrder}
          onChange={onNodeOrderChange}
          disabled={disabled}
        />
      </div>
      <div>
        <div style={sectionTitle}>Part B — Draw the flow links</div>
        <p style={helpText}>
          Click a stage, then click where work goes next to draw an arrow.
          Click the same pair again to remove the link. This is separate from ordering —
          it shows how work moves between agents (including escalations).
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <button type="button" style={btnSecondary} onClick={suggestFromOrder} disabled={disabled}>
            Suggest links from order
          </button>
          <button type="button" style={btnSecondary} onClick={() => { setLinkFrom(null); onEdgesChange([]); }} disabled={disabled}>
            Clear links
          </button>
          {linkFrom ? (
            <span style={hintBox}>Selected: {nodes.find((n) => n.id === linkFrom)?.label || linkFrom} — click a target</span>
          ) : null}
        </div>
        <div style={{ position: 'relative', overflowX: 'auto', paddingBottom: 8 }}>
          <svg width={Math.max(320, orderedNodes.length * 150)} height={140} style={{ display: 'block' }}>
            {edges.map(([a, b]) => {
              const p1 = positions[a];
              const p2 = positions[b];
              if (!p1 || !p2) return null;
              const mx = (p1.x + p2.x) / 2;
              const my = Math.min(p1.y, p2.y) - 28;
              return (
                <g key={`${a}-${b}`}>
                  <path
                    d={`M ${p1.x + 50} ${p1.y} Q ${mx} ${my} ${p2.x + 50} ${p2.y}`}
                    stroke="#38bdf8"
                    strokeWidth="2.5"
                    fill="none"
                    markerEnd="url(#arrowhead)"
                  />
                </g>
              );
            })}
            <defs>
              <marker id="arrowhead" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                <polygon points="0 0, 8 3, 0 6" fill="#38bdf8" />
              </marker>
            </defs>
          </svg>
          <div style={{ display: 'flex', gap: 16, marginTop: -90, paddingLeft: 20, minWidth: orderedNodes.length * 150 }}>
            {orderedNodes.map((n) => (
              <button
                key={n.id}
                type="button"
                disabled={disabled}
                onClick={() => toggleLink(n.id)}
                style={{
                  ...graphNodeBtn,
                  ...(linkFrom === n.id ? graphNodeBtnActive : {}),
                }}
              >
                {n.label}
              </button>
            ))}
          </div>
        </div>
        <ul style={{ ...listStyle, marginTop: 14 }}>
          {edges.length === 0 ? (
            <li style={{ ...listItemStyle, color: '#94a3b8' }}>No links yet — click two stages to connect them.</li>
          ) : edges.map(([a, b]) => (
            <li key={`${a}-${b}`} style={listItemStyle}>
              <span style={{ flex: 1 }}>
                {(nodes.find((n) => n.id === a)?.label || a)} → {(nodes.find((n) => n.id === b)?.label || b)}
              </span>
              <button type="button" style={btnSm} disabled={disabled} onClick={() => onEdgesChange(edges.filter((e) => !(e[0] === a && e[1] === b)))}>Remove</button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function BalanceView({ sliders, values, onChange, disabled }) {
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {sliders.map((s) => (
        <label key={s.id} style={{ display: 'grid', gap: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#e2e8f0', fontSize: 14 }}>
            <span>{s.label}</span>
            <strong>{values[s.id] ?? s.default ?? 50}</strong>
          </div>
          <input
            type="range"
            min={s.min ?? 0}
            max={s.max ?? 100}
            step={s.step ?? 1}
            value={values[s.id] ?? s.default ?? 50}
            disabled={disabled}
            onChange={(e) => onChange({ ...values, [s.id]: Number(e.target.value) })}
          />
        </label>
      ))}
    </div>
  );
}

function initAnswerState(fmt, payload) {
  if (fmt === 'sequence_order') {
    const items = payload.items || payload.steps || [];
    const ids = items.map((i) => i.id);
    return { order: seededShuffle(ids, `seq:${ids.join(',')}`) };
  }
  if (fmt === 'pair_match') return { pairs: [] };
  if (fmt === 'constraint_select' || fmt === 'defect_hunt') return { selected: [] };
  if (fmt === 'fault_find') return { fault_ids: [] };
  if (fmt === 'classify_act') return { risk: '', action: '' };
  if (fmt === 'label_diagram') return { labels: {} };
  if (fmt === 'graph_assemble') {
    const nodes = payload.nodes || [];
    return {
      edges: [],
      node_order: seededShuffle(nodes.map((n) => n.id), `graph:${nodes.map((n) => n.id).join(',')}`),
    };
  }
  if (fmt === 'parameter_balance') {
    const sliders = payload.sliders || [];
    const values = {};
    sliders.forEach((s) => { values[s.id] = s.default ?? 50; });
    return { values };
  }
  return {};
}

export default function PuzzlePlayer({
  scene,
  domain = 'ai',
  tutorialId,
  segmentId,
  onComplete,
  onSolvedChange,
}) {
  const sceneId = scene?.scene_id || resolveMeta(scene).scene_id;
  const [remoteScene, setRemoteScene] = useState(null);
  const [loadError, setLoadError] = useState('');

  // Prefer embedded segment metadata; fall back to GET /puzzles/{id} when
  // format_payload is missing (common after navigate away/back if tutorial
  // segment was stored without a full payload).
  useEffect(() => {
    const embedded = resolveMeta(scene);
    if (embedded?.format_payload && Object.keys(embedded.format_payload).length) {
      setRemoteScene(null);
      setLoadError('');
      return;
    }
    if (!sceneId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/${encodeURIComponent(sceneId)}`, {
          headers: authHeaders(),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || `Failed to load puzzle (${res.status})`);
        if (!cancelled) setRemoteScene(data);
      } catch (err) {
        if (!cancelled) setLoadError(err.message || String(err));
      }
    })();
    return () => { cancelled = true; };
  }, [sceneId, scene]);

  const effectiveScene = remoteScene || scene;
  const meta = useMemo(() => resolveMeta(effectiveScene), [effectiveScene]);
  const fmt = meta.puzzle_format || 'sequence_order';
  const payload = meta.format_payload || {};
  const title = cleanPuzzleTitle(
    effectiveScene?.title || effectiveScene?.display_name || scene?.title || meta.stem || 'Puzzle',
  );

  const shuffledRight = useMemo(
    () => seededShuffle(payload.right || [], `${sceneId}:right`),
    [sceneId, JSON.stringify((payload.right || []).map((r) => r.id))], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const shuffledLabels = useMemo(
    () => seededShuffle(payload.labels || [], `${sceneId}:labels`),
    [sceneId, JSON.stringify((payload.labels || []).map((l) => l.id))], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const shuffledOptions = useMemo(
    () => seededShuffle(payload.options || [], `${sceneId}:options`),
    [sceneId, JSON.stringify((payload.options || []).map((o) => o.id || o))], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const shuffledDefects = useMemo(() => {
    const raw = (payload.defects || payload.options || []).map((d) => (
      typeof d === 'string' ? { id: d, label: d } : d
    ));
    return seededShuffle(raw, `${sceneId}:defects`);
  }, [sceneId, JSON.stringify(payload.defects || payload.options || [])]); // eslint-disable-line react-hooks/exhaustive-deps
  const shuffledFaults = useMemo(() => {
    const raw = (payload.steps || payload.options || []).map((d) => (
      typeof d === 'string' ? { id: d, label: d } : d
    ));
    return seededShuffle(raw, `${sceneId}:faults`);
  }, [sceneId, JSON.stringify(payload.steps || payload.options || [])]); // eslint-disable-line react-hooks/exhaustive-deps
  const shuffledRisk = useMemo(
    () => seededShuffle(payload.risk_options || [], `${sceneId}:risk`),
    [sceneId, JSON.stringify((payload.risk_options || []).map((o) => o.id))], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const shuffledAction = useMemo(
    () => seededShuffle(payload.action_options || [], `${sceneId}:action`),
    [sceneId, JSON.stringify((payload.action_options || []).map((o) => o.id))], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const [answerState, setAnswerState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Restore draft / initialize when scene + payload are ready
  useEffect(() => {
    if (!payload || (fmt === 'pair_match' && !(payload.left || []).length)) {
      // Still waiting for remote payload
      if (!remoteScene && !loadError) return;
    }
    if (!sceneId) {
      setAnswerState(initAnswerState(fmt, payload));
      setResult(null);
      setError('');
      return;
    }
    const draft = loadDraft(sceneId);
    if (draft?.answerState) {
      setAnswerState(draft.answerState);
      setResult(draft.result || null);
    } else {
      setAnswerState(initAnswerState(fmt, payload));
      setResult(null);
    }
    setError('');
  }, [sceneId, fmt, remoteScene, loadError, JSON.stringify(payload.left || [])]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist draft whenever answers / result change
  useEffect(() => {
    if (!sceneId || !answerState) return;
    saveDraft(sceneId, answerState, result);
  }, [sceneId, answerState, result]);

  const buildAnswer = useCallback(() => {
    if (!answerState) return {};
    if (fmt === 'sequence_order') return { order: answerState.order };
    if (fmt === 'pair_match') return { pairs: answerState.pairs };
    if (fmt === 'constraint_select') return { selected: answerState.selected };
    if (fmt === 'defect_hunt') return { defect_ids: answerState.selected };
    if (fmt === 'fault_find') return { fault_ids: answerState.fault_ids };
    if (fmt === 'classify_act') return { risk: answerState.risk, action: answerState.action };
    if (fmt === 'label_diagram') return { labels: answerState.labels };
    if (fmt === 'graph_assemble') {
      return { edges: answerState.edges, node_order: answerState.node_order };
    }
    if (fmt === 'parameter_balance') return { values: answerState.values };
    return answerState;
  }, [answerState, fmt]);

  const submit = async () => {
    if (!sceneId) {
      setError('Missing scene_id for this puzzle.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/${encodeURIComponent(sceneId)}/check`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          answer: buildAnswer(),
          tutorial_id: tutorialId,
          segment_id: segmentId,
          domain,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Check failed (${res.status})`);
      setResult(data);
      onSolvedChange?.(!!data.solved);
      // Intentionally do NOT call onComplete here — show feedback first.
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const tryAgain = () => {
    setResult(null);
    setError('');
  };

  if (loadError) {
    return <div style={errBox}>{loadError}</div>;
  }
  if (!answerState) {
    return <div style={muted}>Loading puzzle…</div>;
  }

  const solved = !!result?.solved;
  const locked = solved;

  let body = null;
  if (fmt === 'sequence_order') {
    body = (
      <SequenceView
        items={payload.items || payload.steps || []}
        order={answerState.order}
        onChange={(order) => setAnswerState({ order })}
        disabled={locked}
      />
    );
  } else if (fmt === 'pair_match') {
    body = (
      <PairMatchView
        left={payload.left || []}
        right={shuffledRight}
        pairs={answerState.pairs || []}
        onChange={(pairs) => setAnswerState({ pairs })}
        disabled={locked}
        pairResults={result?.pair_results}
      />
    );
  } else if (fmt === 'constraint_select') {
    body = (
      <SelectSetView
        options={shuffledOptions}
        selected={answerState.selected}
        onChange={(selected) => setAnswerState({ selected })}
        disabled={locked}
      />
    );
  } else if (fmt === 'defect_hunt') {
    body = (
      <SelectSetView
        options={shuffledDefects}
        selected={answerState.selected}
        onChange={(selected) => setAnswerState({ selected })}
        disabled={locked}
      />
    );
  } else if (fmt === 'fault_find') {
    body = (
      <SelectSetView
        options={shuffledFaults}
        selected={answerState.fault_ids}
        onChange={(fault_ids) => setAnswerState({ fault_ids })}
        multi={!!payload.multi}
        disabled={locked}
      />
    );
  } else if (fmt === 'classify_act') {
    body = (
      <ClassifyView
        riskOptions={shuffledRisk}
        actionOptions={shuffledAction}
        risk={answerState.risk}
        action={answerState.action}
        onRisk={(risk) => setAnswerState((s) => ({ ...s, risk }))}
        onAction={(action) => setAnswerState((s) => ({ ...s, action }))}
        disabled={locked}
      />
    );
  } else if (fmt === 'label_diagram') {
    body = (
      <LabelDiagramView
        hotspots={payload.hotspots || []}
        labels={shuffledLabels}
        mapping={answerState.labels}
        onChange={(labels) => setAnswerState({ labels })}
        disabled={locked}
      />
    );
  } else if (fmt === 'graph_assemble') {
    body = (
      <GraphAssembleView
        nodes={payload.nodes || []}
        edges={answerState.edges}
        onEdgesChange={(edges) => setAnswerState((s) => ({ ...s, edges }))}
        nodeOrder={answerState.node_order}
        onNodeOrderChange={(node_order) => setAnswerState((s) => ({ ...s, node_order }))}
        disabled={locked}
      />
    );
  } else if (fmt === 'parameter_balance') {
    body = (
      <BalanceView
        sliders={payload.sliders || []}
        values={answerState.values}
        onChange={(values) => setAnswerState({ values })}
        disabled={locked}
      />
    );
  } else {
    body = <div style={muted}>Unsupported puzzle format: {fmt}</div>;
  }

  return (
    <div style={shell} data-testid="puzzle-player">
      <div style={badgeRow}>
        <span style={badge}>{fmt.replace(/_/g, ' ')}</span>
        {meta.learning_objective ? (
          <span style={objective}>{meta.learning_objective}</span>
        ) : null}
      </div>
      {/* Title shown once inside the puzzle shell (parent may also show a cleaned segment title) */}
      <h3 style={titleStyle}>{title}</h3>
      {meta.stem ? <p style={stem}>{meta.stem}</p> : null}
      {payload.constraints ? (
        <div style={constraintBox}>
          <strong>Constraints</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {(payload.constraints || []).map((c) => (
              <li key={String(c)}>{typeof c === 'string' ? c : c.label || JSON.stringify(c)}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {payload.spec_text ? (
        <pre style={specBox}>{payload.spec_text}</pre>
      ) : null}
      <div style={{ marginTop: 16 }}>{body}</div>
      <div style={{ marginTop: 20, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {!solved ? (
          <button type="button" style={btnPrimary} onClick={submit} disabled={busy}>
            {busy ? 'Checking…' : result ? 'Check again' : 'Check answer'}
          </button>
        ) : null}
        {result && !solved ? (
          <button type="button" style={btnSecondary} onClick={tryAgain}>
            Edit answers
          </button>
        ) : null}
        {solved ? (
          <button
            type="button"
            style={btnPrimary}
            onClick={() => onComplete?.(result)}
          >
            Continue →
          </button>
        ) : null}
        {result?.hint && !solved ? (
          <span style={hintBox}>Hint: {result.hint}</span>
        ) : null}
      </div>
      {error ? <div style={errBox}>{error}</div> : null}
      {result ? (
        <div style={solved ? okBox : warnBox}>
          <div style={{ fontWeight: 700 }}>{solved ? 'Correct!' : 'Not quite — review the matches below'}</div>
          <div>{result.feedback}</div>
          {result.explanation ? <p style={{ marginTop: 8 }}>{result.explanation}</p> : null}
          {result.xp_awarded > 0 ? <div style={{ marginTop: 6 }}>+{result.xp_awarded} XP</div> : null}
        </div>
      ) : null}
    </div>
  );
}

const shell = {
  background: 'rgba(15,23,42,0.9)',
  border: '1px solid rgba(148,163,184,0.25)',
  borderRadius: 12,
  padding: 20,
  color: '#e2e8f0',
};
const titleStyle = { margin: '8px 0', fontSize: 20, fontWeight: 700, color: '#f8fafc' };
const stem = { margin: '0 0 8px', color: '#cbd5e1', lineHeight: 1.5, fontSize: 15 };
const muted = { color: '#94a3b8', padding: 24 };
const badgeRow = { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' };
const badge = {
  textTransform: 'capitalize',
  fontSize: 11,
  letterSpacing: 0.4,
  padding: '4px 8px',
  borderRadius: 999,
  background: 'rgba(56,189,248,0.15)',
  color: '#7dd3fc',
  border: '1px solid rgba(56,189,248,0.35)',
};
const objective = { fontSize: 12, color: '#94a3b8' };
const listStyle = { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 };
const listItemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 12px',
  borderRadius: 8,
  background: 'rgba(30,41,59,0.9)',
  border: '1px solid rgba(148,163,184,0.2)',
};
const btnSm = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.12)',
  color: '#e2e8f0',
  borderRadius: 6,
  padding: '4px 8px',
  cursor: 'pointer',
};
const btnPrimary = {
  background: '#2563eb',
  border: 'none',
  color: '#fff',
  borderRadius: 8,
  padding: '10px 16px',
  fontWeight: 600,
  cursor: 'pointer',
};
const btnSecondary = {
  background: 'transparent',
  border: '1px solid rgba(148,163,184,0.45)',
  color: '#e2e8f0',
  borderRadius: 8,
  padding: '10px 16px',
  fontWeight: 600,
  cursor: 'pointer',
};
const selectStyle = {
  flex: 1,
  minWidth: 160,
  background: '#0f172a',
  color: '#e2e8f0',
  border: '1px solid rgba(148,163,184,0.35)',
  borderRadius: 8,
  padding: '8px 10px',
};
const rowLabel = { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' };
const optionBtn = {
  textAlign: 'left',
  padding: '12px 14px',
  borderRadius: 8,
  border: '1px solid rgba(148,163,184,0.25)',
  background: 'rgba(30,41,59,0.85)',
  color: '#e2e8f0',
  cursor: 'pointer',
};
const optionBtnOn = {
  border: '1px solid rgba(56,189,248,0.7)',
  background: 'rgba(14,165,233,0.18)',
};
const metaHint = { display: 'block', fontSize: 12, color: '#94a3b8', marginTop: 4 };
const sectionTitle = { fontSize: 13, fontWeight: 700, color: '#94a3b8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 };
const constraintBox = {
  marginTop: 10,
  padding: 12,
  borderRadius: 8,
  background: 'rgba(245,158,11,0.08)',
  border: '1px solid rgba(245,158,11,0.3)',
  fontSize: 13,
};
const specBox = {
  marginTop: 10,
  padding: 12,
  borderRadius: 8,
  background: '#020617',
  border: '1px solid rgba(148,163,184,0.25)',
  fontSize: 12,
  whiteSpace: 'pre-wrap',
  color: '#cbd5e1',
  overflow: 'auto',
};
const hintBox = {
  fontSize: 13,
  color: '#fde68a',
  background: 'rgba(245,158,11,0.12)',
  border: '1px solid rgba(245,158,11,0.35)',
  borderRadius: 8,
  padding: '8px 12px',
};
const errBox = { marginTop: 12, color: '#fecaca', fontSize: 13 };
const okBox = {
  marginTop: 14,
  padding: 12,
  borderRadius: 8,
  background: 'rgba(16,185,129,0.12)',
  border: '1px solid rgba(16,185,129,0.4)',
  color: '#d1fae5',
};
const warnBox = {
  marginTop: 14,
  padding: 12,
  borderRadius: 8,
  background: 'rgba(245,158,11,0.1)',
  border: '1px solid rgba(245,158,11,0.35)',
  color: '#fde68a',
};
const pairExplainWrong = {
  marginTop: 6,
  marginLeft: 4,
  fontSize: 12,
  color: '#fecaca',
  lineHeight: 1.45,
};
const pairExplainOk = {
  marginTop: 6,
  marginLeft: 4,
  fontSize: 12,
  color: '#a7f3d0',
  lineHeight: 1.45,
};
const helpText = {
  margin: '0 0 10px',
  fontSize: 13,
  color: '#94a3b8',
  lineHeight: 1.45,
};
const graphNodeBtn = {
  minWidth: 110,
  padding: '14px 12px',
  borderRadius: 10,
  border: '1px solid rgba(148,163,184,0.4)',
  background: 'rgba(30,41,59,0.95)',
  color: '#f8fafc',
  fontWeight: 600,
  cursor: 'pointer',
  zIndex: 1,
};
const graphNodeBtnActive = {
  border: '2px solid #38bdf8',
  background: 'rgba(14,165,233,0.25)',
  boxShadow: '0 0 0 3px rgba(56,189,248,0.2)',
};
